import React, { useEffect, useState } from "react";
import { NextPage, NextPageContext } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import AudioDashboard from "@audio/AudioDashboard";
import audioPlayerManager from "@audio/AudioPlayerManager/AudioPlayerManager";
import OopsPage from "@pageContent/OopsPage";
import VideoPlayerPortal from "@video/VideoPlayerPortal";
import { AudioType, ROUTE_PATHS, tableNames } from "@constants/consts";
import { PlaceHolderAudioDashboardSkeleton } from "@pages/zenders/[slug]";
import { fetchCurrentShow } from "@utils/audio/audioApi";
import { Show } from "@utils/audio/dataModels";
import { useAudioEntry } from "@utils/audio/useAudioEntry";
import { createRedirectResponse } from "@utils/common/createRedirectResponse";
import DynamoDBUtils from "@utils/common/dynamo";
import getHostConfig, { useHostConfig } from "@utils/common/getHostConfig";
import usePageView from "@utils/common/usePageView";

const PopOutPage: NextPage<{
  initialStation?: AudioStationEntry | null;
  initialShow?: Show | null;
}> = ({ initialStation, initialShow }) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const {
    query: { stationSlug },
  } = useRouter();
  const { mainStationSlug } = useHostConfig();

  const {
    isLoading,
    data: station,
    isError,
    error,
  } = useAudioEntry<AudioStationEntry>({
    audioType: AudioType.STATION,
    slug: stationSlug as string,
    initialData: initialStation,
  });

  usePageView({ isLoading, error, data: station });

  useEffect(() => {
    if (station) {
      audioPlayerManager.load(station);
      audioPlayerManager.start(station.mountSource);
    }
  }, [station]);

  useEffect(() => {
    const handleToggleLiveVideo = (data: WebXEventParams["radioLiveVideo"]) => setIsVideoPlaying(data.isPlaying);

    window.eventBus.on("radioLiveVideo", handleToggleLiveVideo, true);

    return () => {
      window.eventBus.off("radioLiveVideo", handleToggleLiveVideo);
    };
  }, []);

  return (
    <>
      <Head>
        <meta name="robots" content="noindex" />
      </Head>
      {!station ? (
        isLoading ? (
          <PlaceHolderAudioDashboardSkeleton />
        ) : (
          <OopsPage is404={!isError} error={error} />
        )
      ) : (
        <>
          <AudioDashboard station={station} initialShow={initialShow} />
          {isVideoPlaying && station.slug === mainStationSlug && (
            <VideoPlayerPortal inDashboardView mainStation={station} />
          )}
        </>
      )}
    </>
  );
};

PopOutPage.getInitialProps = async ({ req, res, query }: NextPageContext) => {
  if (!res || !req) return {};

  const { mainStationSlug } = getHostConfig();
  const { stationSlug } = query;
  const redirectionOptions = {
    res,
    query,
    url: mainStationSlug ? `${ROUTE_PATHS.POPOUT}${mainStationSlug}` : ROUTE_PATHS.HOME_PAGE,
  };

  if (!stationSlug) {
    return createRedirectResponse(redirectionOptions);
  }

  try {
    const [initialStation, initialShow] = await Promise.all([
      DynamoDBUtils.getItemBySlug<AudioStationEntry>(tableNames.audio, stationSlug as string),
      fetchCurrentShow(stationSlug as string),
    ]);

    if (!initialStation) {
      return createRedirectResponse(redirectionOptions);
    }

    return { initialStation, initialShow };
  } catch (error) {
    res.statusCode = 500;
    return {};
  }
};

export default PopOutPage;
